import React, {createContext, useContext, useMemo} from 'react';

import {useRunSetProject} from '../../state/views/runSet/hooks';
import type {RunSetPart} from '../../state/views/runSet/types';

type RunSelectorProjectContextType = {
  entityName: string;
  projectName: string;
  projectInternalId?: string;
};

const RunSelectorProjectContext =
  createContext<RunSelectorProjectContextType | null>(null);

export function useRunSelectorProjectInfo(): RunSelectorProjectContextType {
  const ctx = useContext(RunSelectorProjectContext);
  if (!ctx) {
    throw new RangeError(`Missing RunSelectorProjectContext in tree`);
  }
  return ctx;
}

type RunSetProjectProviderProps = {
  runSetProject: RunSetPart['project'];
  children: React.ReactNode;
};

/**
 * Adds project info to the Run Selector context. This adds entityName,
 * projectName, and projectInternalId to the context, which are derived from
 * the runset's project when possible, or from the URL params as a fallback.
 *
 * @param runSetProject
 * @param children
 */
export function RunSelectorProjectProvider({
  runSetProject,
  children,
}: RunSetProjectProviderProps) {
  const {entityName, projectName, projectInternalId} =
    useRunSetProject(runSetProject);

  const value = useMemo(
    () => ({entityName, projectName, projectInternalId}),
    [entityName, projectName, projectInternalId]
  );

  return (
    <RunSelectorProjectContext.Provider value={value}>
      {children}
    </RunSelectorProjectContext.Provider>
  );
}
